import { signal } from '@angular/core';
import { FieldTree, SchemaPathTree } from '@angular/forms/signals';
import { BuriedDto, FactorsERA6930P5Dto } from '../../../../core/mceapi.client.generated';
import { apiOpt, appOpt, getNoneOptionDto, getSomeOptionDto, isSomeOptionDto } from '../../../../core/mceapi.client.helpers';
import { match } from '../../../../shared/functions/functional';
import { AmbientTemperature, buildAmbientTemperatureSchema, createAmbientTemperature, createAmbientTemperatureDto, defaultAmbientTemperature } from '../ambient-temperature/ambient-temperature.model';
import { buildBuriedSchema, Buried, createBuried, createBuriedDto, defaultBuried } from '../buried/buried.model';
import { buildGroupingSchema, createGrouping, createGroupingDto, defaultGrouping, Grouping } from '../grouping/grouping.model';
import { buildUserDefinedFactorSchema, createUserDefinedFactor, createUserDefinedFactorDto, defaultUserDefinedFactor, UserDefinedFactor } from '../user-defined-factor/user-defined-factor.model';


export interface FactorsERA6930P5 {
  primaryMethodGuid: string,
  secondaryMethodGuid: string,
  ambientTemperature: AmbientTemperature,
  grouping: Grouping,
  buried: Buried,
  userDefinedFactor: UserDefinedFactor,
  isBuried: boolean;
}

export function defaultFactorsERA6930P5(defaultAirTemperature: number, defaultGroundTemperature: number, defaultSoilThermalResistivtiy: number): FactorsERA6930P5 {
  return {
    primaryMethodGuid: '',
    secondaryMethodGuid: '',
    ambientTemperature: defaultAmbientTemperature(defaultAirTemperature, defaultGroundTemperature),
    grouping: defaultGrouping(),
    buried: defaultBuried(defaultSoilThermalResistivtiy),
    userDefinedFactor: defaultUserDefinedFactor(),
    isBuried: false,
  };
}

export function buildFactorsERA6930P5Schema(path: SchemaPathTree<FactorsERA6930P5>) {
  buildAmbientTemperatureSchema(path.ambientTemperature);
  buildGroupingSchema(path.grouping);
  buildBuriedSchema(path.buried);
  buildUserDefinedFactorSchema(path.userDefinedFactor);
}

export function createFactorsERA6930P5(dto: FactorsERA6930P5Dto, defaultSoilThermalResistivtiy: number) {
  const primaryMethodGuid = dto.primaryMethodGuid;
  const secondaryMethodGuid = dto.secondaryMethodGuid;
  const ambientTemperature = dto.ambientTemperature;
  const grouping = dto.grouping;

  if (primaryMethodGuid === undefined
    || secondaryMethodGuid === undefined
    || ambientTemperature === undefined
    || grouping === undefined) {
    throw new Error('ERA 69-30 Part 5 factors DTO is missing required values.');
  }

  return signal<FactorsERA6930P5>({
    primaryMethodGuid,
    secondaryMethodGuid,
    ambientTemperature: createAmbientTemperature(ambientTemperature)(),
    grouping: createGrouping(grouping)(),
    buried:
      match(
        () => ({ depth: 0.9, soilThermalResistivity: defaultSoilThermalResistivtiy }),
        (x: BuriedDto) => createBuried(x)())
        (appOpt(dto.buried ?? {})),
    userDefinedFactor: createUserDefinedFactor(appOpt(dto.userDefinedFactor ?? {}))(),
    isBuried: isSomeOptionDto(dto.buried ?? {}),
  });
}

export function createFactorsERA6930P5Dto(model: FieldTree<FactorsERA6930P5>) {
  return signal<FactorsERA6930P5Dto>({
    primaryMethodGuid: model.primaryMethodGuid().value(),
    secondaryMethodGuid: model.secondaryMethodGuid().value(),
    ambientTemperature: createAmbientTemperatureDto(model.ambientTemperature)(),
    grouping: createGroupingDto(model.grouping)(),
    buried:
      model.isBuried().value()
        ? getSomeOptionDto<BuriedDto>(createBuriedDto(model.buried)())
        : getNoneOptionDto<BuriedDto>(),
    userDefinedFactor: apiOpt(createUserDefinedFactorDto(model.userDefinedFactor)())
  });
}

